import React, { useContext } from 'react';
import './index.scss';
import { withTheme } from '@ifit/themes/withTheme';
import { ThemeContext } from '@ifit/themes/ThemeContext';
import ContentItem, {
  IContentItem,
  IContentItemAnalytics,
} from '../content-item';
import { WeakType } from '../../typescript-migration';
import { useDeviceType } from '../../store/device-type';
import GridList from '@ifit/mp-gridlist/dist';
import Loading from '@ifit/mp-loading';
import SectionHeader from '@ifit/mp-section-header';
import { loadingText, challengesLoadingText } from '../../i18n/translations';
import LONG_ARROW_DOWN_LINE from '@ifit/react-icons/dist/icons/LONG_ARROW_DOWN_LINE';
import { shark, solidWhite } from '../../theme';
import Icon from '@ifit/react-icons/dist/components/icon';
import cx from 'classnames';
import { isMobile } from '../../helpers';

interface IDispatchProps {
  onSortingChanged?: (sortLabel: string) => void;
  onTypeOptionsChanged?: () => void;
}

interface IOwnProps {
  title?: string;
  maxCharacters?: number;
  libraryCards?: IContentItem[];
  sortOptions?: WeakType[];
  selectedSortOption?: number;
  scrollLoading?: boolean;
  hasMoreItems?: boolean;
  infiniteButtonEnable?: boolean;
  infiniteButton?: () => void;
  error?: boolean;
  showCategory?: boolean;
  showSubcategory?: boolean;
  disableAvatars?: boolean;
  typeOptions?: WeakType;
  selectedType?: WeakType;
  themeClass?: string;
  subtitle?: string;
  analytics?: IContentItemAnalytics;
  libraryBadges?: WeakType;
  containsLibraryBadges?: boolean;
}

interface IProps extends IDispatchProps, IOwnProps {}

const SectionHeaderPlain = SectionHeader();

const GridSection: React.FC<IProps> = ({
  title,
  maxCharacters,
  sortOptions,
  error,
  selectedSortOption,
  onSortingChanged,
  typeOptions,
  onTypeOptionsChanged,
  selectedType,
  libraryCards,
  showSubcategory,
  showCategory,
  disableAvatars,
  scrollLoading,
  hasMoreItems,
  infiniteButton,
  infiniteButtonEnable,
  themeClass,
  subtitle,
  analytics,
  libraryBadges,
  containsLibraryBadges,
}) => {
  const deviceType = useDeviceType();
  const shouldShowHeader = title || sortOptions;
  const { theme } = useContext(ThemeContext);
  const arrowColor = {
    LIGHT: shark,
    DARK: solidWhite,
    VAULT: shark,
  }[theme];

  const loading = (
    <>
      <div className="loader">{scrollLoading && <Loading absolute />}</div>
      <p className="loading">{loadingText()}</p>
    </>
  );

  const infiniteButtonContainer = (
    <div className="infinite-button-container">
      {scrollLoading ? (
        <div className="infinite-button-loading">{loading}</div>
      ) : (
        <button className="infinite-button" onClick={infiniteButton}>
          <Icon
            icon={LONG_ARROW_DOWN_LINE}
            color={arrowColor}
            size={20}
            unitSize="px"
            style={{
              display: 'inline-block',
              verticalAlign: 'middle',
              marginRight: '14px',
            }}
          />
          {challengesLoadingText()}
        </button>
      )}
    </div>
  );

  const loadingMore = infiniteButtonEnable ? (
    infiniteButtonContainer
  ) : (
    <div
      className="contain-loading"
      style={{
        opacity: scrollLoading ? '1' : '0',
        visibility: scrollLoading ? 'visible' : 'hidden',
      }}
    >
      {loading}
    </div>
  );

  const cardGap = isMobile(deviceType) ? 8 : 24;
  const badgeGap = 3;

  return (
    <section
      style={{ marginBottom: error ? '100px' : null }}
      className={cx('section', { 'no-title': !shouldShowHeader })}
    >
      {shouldShowHeader && (
        <SectionHeaderPlain
          subtitle={subtitle}
          title={title}
          maxCharacters={maxCharacters}
          selectedSortOption={selectedSortOption}
          sortOptions={sortOptions}
          onSortingChanged={onSortingChanged}
          deviceType={deviceType}
          typeOptions={typeOptions}
          onTypeOptionsChanged={onTypeOptionsChanged}
          selectedType={selectedType}
        />
      )}
      <GridList
        data={
          containsLibraryBadges
            ? libraryBadges
            : libraryCards?.map((l, index) => ({ ...l, index: index + 1 }))
        }
        minWidth={null}
        useVirtual={isMobile(deviceType)}
        deviceType={deviceType}
        gridGap={containsLibraryBadges ? badgeGap : cardGap}
        containsLibraryCards={!containsLibraryBadges}
        containsLibraryBadges={containsLibraryBadges}
        themeClass={themeClass}
      >
        {containsLibraryBadges
          ? badges => badges
          : item => (
              <ContentItem
                showCategory={showCategory}
                showSubcategory={showSubcategory}
                item={item}
                disableAvatar={disableAvatars}
                analyticsData={{
                  ...analytics,
                  engagementData: {
                    ...analytics?.engagementData,
                    columnNumber: item.index,
                  },
                }}
              />
            )}
      </GridList>
      {error ? (
        <div className="contain-height">
          <div className="contain-error">
            An error occurred while loading more workouts. Swipe up to try
            again.
          </div>
        </div>
      ) : hasMoreItems ? (
        loadingMore
      ) : null}
    </section>
  );
};

export default withTheme(GridSection);
